// React
import React, { useEffect, useMemo, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';

// Hooks and Redux
import { useAppSelector } from '../../../redux/hooks';

// MUI Components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// MUI Icons
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

// Utils
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastContainer, toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useTranslation } from "react-i18next";
import jwtDecode from 'jwt-decode';

// Styles
import 'react-toastify/dist/ReactToastify.css';
import { green } from '@mui/material/colors';

// Types
import RoleType from '../../../@types/RoleType';

// Services
// import { getAllSites } from '../../../services/SiteService'; //เปลี่ยนมาใช้ useAppSelector
import { createRole } from '../../../services/RoleService';
import { getAllMenus } from '../../../services/MenuService';
import { Menu, Site, TokenPayload } from '../../../@types';

function CreateRole() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate()

  const userRoleType = useAppSelector((state) => state.user.roletype);
  const { menus } = useAppSelector((state) => state.masterData)

  const havePermission = userRoleType === RoleType.Super_Admin ? true : false

  if (!havePermission) {
    navigate("/forbidden", { replace: true })
  }

  const [roleName, setRoleName] = useState<string>("")
  const [active, setActive] = useState<boolean>(true)
  const [selectedSites, setSelectedSites] = useState<Site[]>([])
  // เปลี่ยนมาใช้ useAppSelector
  // const [siteData, setSiteData] = useState<Site[]>([]);
  const { sites } = useAppSelector((state) => state.masterData)
  const siteData = useMemo(
    () =>
      sites
        .filter((site: Site) => site.is_active === true)
        .sort((a: Site, b: Site) => a.site_id.localeCompare(b.site_id)),
    [sites]
  )
  const [selectedRoleType, setSelectedRoleType] = useState<string>("")
  const [selectedMenus, setSelectedMenus] = useState<Menu[]>([])
  const [allMenuData, setAllMenuData] = useState<Menu[]>([])
  const [menusData, setMenusData] = useState<Menu[]>([])
  const [loadingSites, setLoadingSites] = useState<boolean>(false) // เปลี่ยนมาใช้ useAppSelector (ข้อมูลพร้อมใช้จาก store แล้ว)
  const [loadingMenus, setLoadingMenus] = useState<boolean>(true)
  const [createLoading, setCreateLoading] = useState<boolean>(false)

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await getAllMenus("Active")

        if (response?.status === 200 && response?.data?.menus.length > 0) {
          const menusData = response?.data?.menus
          if (menusData) {
            setAllMenuData(
              menusData?.sort((a: Menu, b: Menu) =>
                a.menu_id.localeCompare(b.menu_id)
              )
            )

            setMenusData(allMenuData)
            setLoadingMenus(false)
          }
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      }
    }
    fetchMenus()
  }, [navigate])

  useEffect(() => {
    if (selectedRoleType === RoleType.HR_Recruiter) {
      //การประกาศงาน 200, ใบสมัครงาน300, การสัมภาษณ์400, เชื่อมต่อระบบ EC 500, ระบบจ้างงานด้วย EXCEL 800
      const ALLOWED_PARENT_IDS = ["200", "300", "400", "500", "800"]

      setMenusData(
        allMenuData.filter((menu: Menu) =>
          ALLOWED_PARENT_IDS.includes(menu.parent_id)
        )
      )

      const defaultSelectedMenus = allMenuData.filter((menu: Menu) =>
        ALLOWED_PARENT_IDS.includes(menu.parent_id)
      )
      setSelectedMenus(defaultSelectedMenus)
    } else if (selectedRoleType === RoleType.Super_Admin) {

      const ALLOWED_PARENT_IDS = [
        "100",
        "200",
        "300",
        "400",
        "500",
        "800",
        "900",
        "1000",
      ]
      const ALLOWED_ADMIN_MENU_IDS = ["601", "602", "603", "604", "714", "715"]

      const isMenuAllowed = (menu: Menu): boolean => {
        if (ALLOWED_PARENT_IDS.includes(menu.parent_id)) return true
        if (
          menu.parent_id === "600" &&
          ALLOWED_ADMIN_MENU_IDS.includes(menu.menu_id)
        )
          return true
        return false
      }

      setMenusData(allMenuData.filter(isMenuAllowed))

      const defaultSelectedMenus = allMenuData.filter(isMenuAllowed)
      setSelectedMenus(defaultSelectedMenus)
    } else if (selectedRoleType === RoleType.Support) {
      setMenusData(allMenuData.filter((menu: Menu) => menu.parent_id !== "600"))
      const defaultSelectedMenus = allMenuData.filter(
        (menu: Menu) => menu.parent_id === "600"
      )
      setSelectedMenus(defaultSelectedMenus)
    }
  }, [selectedRoleType])

   const formValidationSchema = yup.object().shape({
     rolename: yup.string().required(`${t("Role name is required")}`),
     roleType: yup.string().required(`${t("Role type is required")}`),
     //  menu: yup.array().required(`${t("Menu is required")}`),
    //  site: yup.array().required(`${t("Site is required")}`),
   })

   const {
     register,
     handleSubmit,
     formState: { errors },
     setValue,
    //  trigger, // ✅ เพิ่ม trigger
   } = useForm({
     resolver: yupResolver(formValidationSchema),
   })

   const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
     setActive(event.target.checked)
   }

   const onSubmit = (user: any) => {
     const postData = async () => {
       try {
         const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
         const sites = (selectedSites as Site[]).map((site) => site.site_id)
         const menus = selectedMenus.map((menu: any) => menu.menu_id)
         const parentMenus = selectedMenus.map((menu: Menu) => menu.parent_id)
         const distinctParentMenus = Array.from(new Set(parentMenus))

         const selectedMenusCreate = [...menus, ...distinctParentMenus]

         selectedMenusCreate.push("100")
         selectedMenusCreate.push("101")

         selectedMenusCreate.sort((a: string, b: string) => a.localeCompare(b))

         setCreateLoading(true)

         const updatedData = {
           role_name: roleName,
           is_active: active,
           role_type: selectedRoleType,
           site_id: sites,
           created_by: parsedToken.Username,
           menu: selectedMenusCreate,
         }

         const response = await createRole(updatedData)

         if (response) {
           setCreateLoading(false)
           if (!response.data?.success) {
             toast.error(
               i18n.language === "th"
                 ? response.data?.message_th
                 : response.data?.message_en,
               {
                 position: "top-center",
                 autoClose: 3000,
                 hideProgressBar: false,
                 closeOnClick: true,
                 pauseOnHover: false,
                 draggable: true,
                 progress: undefined,
                 theme: "light",
               }
             )
           } else if (response.data?.success) {
             Swal.fire({
               title:
                 i18n.language === "th"
                   ? response.data?.message_th
                   : response.data?.message_en,
               icon: "success",
               showCancelButton: false,
               confirmButtonColor: "#43A047",
               confirmButtonText: "OK!",
               allowOutsideClick: false,
               allowEscapeKey: false,
             }).then((result) => {
               if (result.isConfirmed) {
                 window.location.assign("/admin/masterdata/roles")
               }
             })
           }
         }
       } catch (error) {
         navigate("/servererror", { replace: true })
       }
     }

     postData()
   }

   return (
     <>
       <ToastContainer style={{ width: "400px" }} />

       {havePermission ? (
         <>
           <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
             <Box display="flex" gap={3} sx={{ mb: 3 }}>
               <Box
                 display="flex"
                 component={ReactRouterLink}
                 onClick={() => window.history.back()}
                 sx={{
                   textDecoration: "none",
                   color: "#0d47a1",
                   alignItems: "center",
                   ":hover": {
                     color: "#1976d2",
                   },
                 }}
               >
                 <ChevronLeftIcon fontSize="small" />
                 <Typography sx={{ fontWeight: "bold" }}>
                   {t("Back")}
                 </Typography>
               </Box>
               <Breadcrumbs aria-label="breadcrumb">
                 <Link
                   underline="hover"
                   sx={{ display: "flex", alignItems: "center" }}
                   color="inherit"
                   component={ReactRouterLink}
                   to="/admin"
                 >
                   <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                   {t("Dashboard")}
                 </Link>
                 <Link
                   underline="hover"
                   sx={{ display: "flex", alignItems: "center" }}
                   color="inherit"
                   component={ReactRouterLink}
                   to={"/admin/masterdata/roles"}
                 >
                   <AssignmentIndIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                   {t("RoleMenu")}
                 </Link>
                 <Typography
                   sx={{ display: "flex", alignItems: "center" }}
                   color="text.primary"
                 >
                   <AddCircleOutlineIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                   {t("Create New Role")}
                 </Typography>
               </Breadcrumbs>
             </Box>
           </Box>

           <Box
             sx={{
               display: "flex",
               justifyContent: "space-between",
               alignItems: "center",
               height: "70px",
             }}
           >
             <Typography
               variant="h4"
               sx={{ fontWeight: "bold", textAlign: "left" }}
             >
               {t("Create New Role")}
             </Typography>
           </Box>

           <Box
             component="form"
             noValidate
             sx={{ mt: 3 }}
             onKeyDown={(e: React.KeyboardEvent) =>
               e.key === "Enter" && e.preventDefault()
             }
           >
             <Grid container spacing={3}>
               {/* ✅ Role Name */}
               <Grid item xs={12} sm={6}>
                 <TextField
                   fullWidth
                   id="roleName"
                   label={t("Role Name")}
                   {...register("rolename")}
                   error={!!errors.rolename}
                   value={roleName}
                   onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                     setRoleName(event.target.value)
                     setValue("rolename", event.target.value) // ✅ Sync กับ RHF
                   }}
                 />
                 <Typography
                   variant="inherit"
                   color="error"
                   align="left"
                   pt={1}
                   fontSize={13}
                 >
                   {errors.rolename?.message}
                 </Typography>
               </Grid>

               {/* ✅ Role Type */}
               <Grid item xs={12} sm={6}>
                 <FormControl fullWidth error={!!(errors as any).roleType}>
                   <InputLabel id="roleType">{t("Role Type")}</InputLabel>
                   <Select
                     id="roleType"
                     label={t("Role Type")}
                     value={selectedRoleType}
                     {...register("roleType")}
                     onChange={(event: SelectChangeEvent) => {
                       setSelectedRoleType(event.target.value)
                       setValue("roleType", event.target.value) // ✅ Sync กับ RHF
                     }}
                   >
                     <MenuItem value={RoleType.HR_Recruiter}>
                       {t(RoleType.HR_Recruiter)}
                     </MenuItem>
                     <MenuItem value={RoleType.Super_Admin}>
                       {t(RoleType.Super_Admin)}
                     </MenuItem>
                     {/* <MenuItem value={RoleType.Support}>
                       {t(RoleType.Support)}
                     </MenuItem> */}
                   </Select>
                   {/* ✅ Error Message */}
                   {(errors as any).roleType && (
                     <Typography
                       variant="inherit"
                       color="error"
                       align="left"
                       pt={1}
                       fontSize={13}
                     >
                       {(errors as any).roleType?.message}
                     </Typography>
                   )}
                 </FormControl>
               </Grid>

               {/* ✅ Menu */}
               <Grid item xs={12}>
                 <Autocomplete
                   multiple
                   disableCloseOnSelect
                   id="menu"
                   options={menusData.filter(
                     (menu: Menu) =>
                       menu?.parent_id !== null && menu?.parent_id !== "100"
                   )}
                   value={selectedMenus}
                   isOptionEqualToValue={(option: Menu, value: Menu) =>
                     option.menu_id === value.menu_id
                   }
                   getOptionLabel={(option: Menu) => t(option?.menu_name)}
                   groupBy={(option: Menu) => option?.parent_id}
                   onChange={(_, newValue: Menu[]) => {
                     setSelectedMenus(newValue)
                   }}
                   renderGroup={(params) =>
                     menusData.map((menu: Menu) => {
                       if (menu?.menu_id === params?.group) {
                         return (
                           <div key={menu.menu_id}>
                             <Typography
                               variant="inherit"
                               color="primary"
                               align="left"
                               pl={2}
                               pt={1}
                               fontSize={13}
                             >
                               {t(menu.menu_name)}
                             </Typography>
                             {params.children}
                           </div>
                         )
                       }
                     })
                   }
                   renderOption={(props, option: Menu, { selected }) => (
                     <li {...props}>
                       <Checkbox
                         icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                         checkedIcon={<CheckBoxIcon fontSize="small" />}
                         style={{ marginRight: 8 }}
                         checked={selected}
                       />
                       {t(option.menu_name)}
                     </li>
                   )}
                   renderTags={(value: Menu[], getTagProps) =>
                     value.map((option: Menu, index: number) => (
                       <Chip
                         variant="outlined"
                         icon={<Icon fontSize="small">{option.icon}</Icon>}
                         label={t(option.menu_name)}
                         {...getTagProps({ index })}
                       />
                     ))
                   }
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label={loadingMenus ? `${t("Loading")}...` : t("Menu")}
                       placeholder={t("Menu")!}
                       error={!!(errors as any).menu} // ✅ Error
                       helperText={(errors as any).menu?.message} // ✅ Message
                       InputProps={{
                         ...params.InputProps,
                         endAdornment: (
                           <React.Fragment>
                             {loadingMenus ? (
                               <CircularProgress color="inherit" size={20} />
                             ) : null}
                             {params.InputProps.endAdornment}
                           </React.Fragment>
                         ),
                       }}
                     />
                   )}
                   loading={loadingMenus}
                   loadingText={`${t("Loading")}...`}
                   noOptionsText={
                     selectedMenus.length === 0
                       ? t("Please select role type first")
                       : t("No options found")
                   }
                 />
               </Grid>

               {/* ✅ Site */}
               <Grid item xs={12}>
                 <Autocomplete
                   multiple
                   disableCloseOnSelect
                   limitTags={2}
                   id="site"
                   options={[
                     { site_name: t("Select All"), value: "select-all" },
                     ...siteData,
                   ]}
                   value={selectedSites}
                   getOptionLabel={(option) => option.site_name}
                   isOptionEqualToValue={(option, value) =>
                     option.value === value.value ||
                     option.site_name === value.site_name
                   }
                   onChange={(_, newValue: any) => {
                     if (
                       newValue.find(
                         (option: any) => option.value === "select-all"
                       )
                     ) {
                       if (selectedSites.length === siteData.length) {
                         setSelectedSites([])
                         //  setValue("site", []) // ✅ Sync กับ RHF
                       } else {
                         setSelectedSites(siteData)
                         //  setValue("site", siteData) // ✅ Sync กับ RHF
                       }
                     } else {
                       setSelectedSites(newValue)
                       //  setValue("site", newValue) // ✅ Sync กับ RHF
                     }
                   }}
                   renderOption={(props, option, { selected }) => {
                     if (option.value === "select-all") {
                       return (
                         <li {...props}>
                           <Checkbox
                             icon={
                               <CheckBoxOutlineBlankIcon fontSize="small" />
                             }
                             checkedIcon={<CheckBoxIcon fontSize="small" />}
                             style={{ marginRight: 8 }}
                             checked={selectedSites.length === siteData.length}
                             indeterminate={
                               selectedSites.length > 0 &&
                               selectedSites.length < siteData.length
                             }
                           />
                           <strong>{option.site_name}</strong>
                         </li>
                       )
                     }
                     return (
                       <li {...props}>
                         <Checkbox
                           icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                           checkedIcon={<CheckBoxIcon fontSize="small" />}
                           style={{ marginRight: 8 }}
                           checked={selected}
                         />
                         {option.site_name}
                       </li>
                     )
                   }}
                   renderTags={(value: any, getTagProps) =>
                     value.map((option: Site, index: number) => (
                       <Chip
                         variant="outlined"
                         label={t(option.site_name)}
                         {...getTagProps({ index })}
                       />
                     ))
                   }
                   filterOptions={(options, params) => {
                     const filtered = options.filter((option) =>
                       option.value === "select-all"
                         ? true
                         : option.site_name
                             .toLowerCase()
                             .includes(params.inputValue.toLowerCase())
                     )
                     return filtered
                   }}
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label={loadingSites ? `${t("Loading")}...` : t("Site")}
                       placeholder={t("Site")!}
                       error={!!(errors as any).site} // ✅ Error
                       helperText={(errors as any).site?.message} // ✅ Message
                       InputProps={{
                         ...params.InputProps,
                         endAdornment: (
                           <React.Fragment>
                             {loadingSites ? (
                               <CircularProgress color="inherit" size={20} />
                             ) : null}
                             {params.InputProps.endAdornment}
                           </React.Fragment>
                         ),
                       }}
                     />
                   )}
                   loading={loadingSites}
                   loadingText={`${t("Loading")}...`}
                   noOptionsText={t("No options found")}
                 />
               </Grid>

               {/* ✅ Active */}
               <Grid item xs={12}>
                 <FormControlLabel
                   label={t("Active")}
                   control={
                     <Checkbox
                       checked={active}
                       onChange={handleChange}
                       inputProps={{ "aria-label": "Active" }}
                     />
                   }
                 />
               </Grid>
             </Grid>

             {/* ✅ Buttons */}
             <Grid container spacing={3}>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   type="submit"
                   variant="contained"
                   sx={{ mt: 5, mb: 2, color: "white" }}
                   onClick={handleSubmit(onSubmit)}
                   disabled={createLoading}
                 >
                   {t("Create")}
                   {createLoading && (
                     <CircularProgress
                       size={24}
                       sx={{
                         color: green[500],
                         position: "absolute",
                         zIndex: 999,
                       }}
                     />
                   )}
                 </Button>
               </Grid>
               <Grid item xs={6}>
                 <Button
                   fullWidth
                   variant="outlined"
                   sx={{ mt: 5, mb: 2 }}
                   component={ReactRouterLink}
                   to="/admin/masterdata/roles"
                 >
                   {t("Cancel")}
                 </Button>
               </Grid>
             </Grid>
           </Box>
         </>
       ) : null}
     </>
   )
}

export default CreateRole